import { Error } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, TextField, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useContact from '@/hooks/useContact';
import useStatesAndCounties from '@/hooks/useStatesAndCounties';
import useUser from '@/hooks/useUser';
import { AddressProps, LoqateAddressProps } from '@/models/Address';
import { ContactProps } from '@/models/Contact';
import { CountyProps } from '@/models/County';
import { UserProps, UserType } from '@/models/User';
import { loqateApiKey, USCountryId } from '@/utils/constants';
import { phoneNumberIsValid } from '@/utils/contactUtils';

import Address from '../common/address_components/Address';
import PhoneNumberSelect from '../common/contact_components/PhoneNumberSelect';
import { DialogTitleWithClose } from '../common/DialogTitleWithClose';
import { Email } from '../common/text_input/Email';

const RowWrap = ({ children }: { children: React.ReactElement | React.ReactElement[] }) => {
  return (
    <Box
      width='80%'
      display='flex'
      flexGrow={1}
      flexDirection='row'
      flexWrap='wrap'
      justifyContent='space-between'
      alignItems='start'
      gap={'35px'}
    >
      {children}
    </Box>
  );
};

type RequiredContactField =
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'streetAddress1'
  | 'city'
  | 'countyId'
  | 'stateId'
  | 'countryId'
  | 'zipCode';

interface LoqateControl {
  listen: (eventName: 'error' | 'populate', callback: (payload: unknown) => void) => void;
}

interface LoqateGlobal {
  Address: new (
    fields: Array<{ element: string; field: string; mode?: string | number }>,
    options: {
      key: string;
      suppressAutocomplete: boolean;
      countries: { codesList: string };
      setCountryByIP: boolean;
    }
  ) => LoqateControl;
  fieldMode: {
    POPULATE: string | number;
  };
}

declare global {
  interface Window {
    pca?: LoqateGlobal;
  }
}

function isLoqateAddress(payload: unknown): payload is LoqateAddressProps {
  return payload != null && typeof payload === 'object' && 'Province' in payload;
}

function ADFSignUp() {
  const { authResult, b2cId } = useContext(TokenContext);
  const { allStates, allCountries } = useContext(ConfigContext);
  const { usersAll, userType, updateUserCurrent } = useContext(UsersContext);

  const { getCountiesInState, getStateIdByStateAbbreviation } = useStatesAndCounties();

  const { saveContact } = useContact();
  const { postUser } = useUser();

  const [countiesInState, setCountiesInState] = useState<CountyProps[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);
  const [selectedStateAbbr, setSelectedStateAbbr] = useState<string>('');
  const [phoneError, setPhoneError] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [submitClicked, setSubmitClicked] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(true);
  const [countryCode, setCountryCode] = useState<string>('US');
  const [whichContactFieldsHaveErrors, setWhichContactFieldsHaveErrors] = useState<
    RequiredContactField[]
  >([]);
  const [loqateControl, setLoqateControl] = useState<LoqateControl | null>(null);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  const [contactInfo, setContactInfo] = useState<ContactProps>({
    id: null,
    firstName: null,
    lastName: null,
    phone: null,
    email: '',
    streetAddress1: null,
    streetAddress2: '',
    city: null,
    countyId: null,
    stateId: null,
    countryId: USCountryId,
    zipCode: null,
    consent: false,
    departmentOther: '',
    contactSubTypeId: null,
    departmentTypeId: null,
    title: '',
    linkedIn: '',
    whatsApp: '',
    certification: null,
    yearsExperience: null,
    contactRelationships: [],
  });

  const requiredContactFieldLabels = useMemo<Record<RequiredContactField, string>>(() => {
    return {
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      streetAddress1: 'Street Address 1',
      city: 'City',
      countyId: 'County',
      stateId: 'State',
      countryId: 'Country',
      zipCode: 'Postal Code',
    };
  }, []);

  useEffect(() => {
    setContactInfo((prevState: ContactProps) => {
      if (userType == UserType.NationalProgrammeLead) {
        const countyId = prevState.countyId !== '999' ? '999' : prevState.countyId;
        const stateId = prevState.stateId !== '99' ? '99' : prevState.stateId;

        if (countyId !== prevState.countyId || stateId !== prevState.stateId) {
          return {
            ...prevState,
            countyId,
            stateId,
          };
        }
        return prevState;
      }

      if (prevState.countryId !== USCountryId) {
        return {
          ...prevState,
          countryId: USCountryId,
        };
      }

      return prevState;
    });
  }, [userType]);

  useEffect(() => {
    if (contactInfo?.countryId != null && allCountries != null && allCountries.length > 0) {
      const country = allCountries.find((c) => c.id === contactInfo.countryId);
      setCountryCode(country != null && country.abbrev != null ? country.abbrev : 'US');
    }
  }, [allCountries, contactInfo.countryId]);

  useEffect(() => {
    const cf: RequiredContactField[] = [];
    const requiredContactFields = Object.keys(requiredContactFieldLabels) as RequiredContactField[];
    requiredContactFields.forEach((key) => {
      if (
        contactInfo[key] == null ||
        (typeof contactInfo[key] === 'string' && contactInfo[key] === '')
      ) {
        cf.push(key);
      }
    });

    setWhichContactFieldsHaveErrors(cf);
    const contactHasNulls = cf.length > 0;

    setError(contactHasNulls || phoneError);
  }, [contactInfo, requiredContactFieldLabels, phoneError]);

  const navigate = useNavigate();

  // set state by state abbreviation (loqate autocomplete)
  useEffect(() => {
    if (
      selectedStateAbbr !== '' &&
      selectedStateAbbr.length > 0 &&
      allStates != null &&
      allStates.length > 0
    ) {
      const stateId = getStateIdByStateAbbreviation(selectedStateAbbr, allStates);

      if (stateId != null && stateId !== '') setSelectedStateId(stateId);
    }
  }, [selectedStateAbbr, allStates, getStateIdByStateAbbreviation]);

  // when a new state is selected, clear the counties and set the state id on the contact info object
  useEffect(() => {
    if (selectedStateId != null && contactInfo.stateId != selectedStateId) {
      setCountiesInState([]);
      setContactInfo((prevState: ContactProps) => ({
        ...prevState,
        stateId: selectedStateId,
        countyId: null,
      }));
    }
  }, [selectedStateId, contactInfo.stateId]);

  // when a new state is selected and the counties are cleared out, get the counties in that state
  useEffect(() => {
    if (selectedStateId != null && countiesInState.length == 0) {
      const filteredCounties = getCountiesInState(selectedStateId);
      setCountiesInState(filteredCounties);
    }
  }, [selectedStateId, countiesInState.length, getCountiesInState]);

  const handleSubmit = async () => {
    setFormSubmitted(true);

    if (error) {
      setTimeout(() => {
        setOpenModal(true);
      }, 500);
    } else {
      setSubmitClicked(true);

      const contact = await saveContact(contactInfo);
      if (b2cId != null && contact != null && contact.id != null) {
        const userInfo: UserProps = {
          b2CId: b2cId,
          contactId: contact.id,
          admin: true,
          userType: UserType.Adf,
          userCanImpersonate: true,
          disabled: false,
          validated: true,
          email: contactInfo.email ?? '',
        };

        const user = await postUser(userInfo);
        updateUserCurrent(user);
        navigate('/dashboard');
      }
      setSubmitClicked(false);
    }
  };

  useEffect(() => {
    if (
      authResult != null &&
      usersAll != null &&
      usersAll.length > 0 &&
      (contactInfo.email ?? '') === '' &&
      b2cId != null &&
      b2cId !== ''
    ) {
      const user = usersAll.find((u: UserProps) => u.b2CId == b2cId);
      if (user != null) {
        setContactInfo((prevState: ContactProps) => ({
          ...prevState,
          email: user.email,
        }));
      }
    }
  }, [usersAll, authResult, b2cId, contactInfo.email]);

  useEffect(() => {
    const username = authResult?.account?.username;
    if (username != null && username !== '' && contactInfo.email != username) {
      setContactInfo((prevState: ContactProps) => ({
        ...prevState,
        email: username,
      }));
    }
  }, [authResult, contactInfo.email]);

  const initializeLoqate = useCallback(() => {
    if (loqateControl != null) return;
    const pca = window.pca;
    if (pca == null) return;

    // pca is a global object created by the loqate library which is loaded in index.html file
    const loqateFields = [
      { element: 'streetAddress1', field: 'Line1' },
      {
        element: 'streetAddress2',
        field: 'Line2',
        mode: pca.fieldMode.POPULATE,
      },
      { element: 'city', field: 'City', mode: pca.fieldMode.POPULATE },
      { element: 'zipCode', field: 'PostalCode', mode: pca.fieldMode.POPULATE },
      { element: '', field: 'Company' }, // Make sure company is blank
      // we'll populate these manually
      // { element: "state-select", field: "Province", mode: pca.fieldMode.POPULATE },
      // { element: "county-select", field: "AdminAreaName", mode: pca.fieldMode.POPULATE },
    ];

    const options = {
      key: loqateApiKey,
      suppressAutocomplete: false,
      countries: { codesList: 'US' },
      setCountryByIP: true,
    };

    setLoqateControl(new pca.Address(loqateFields, options));
  }, [loqateControl]);

  useEffect(() => {
    initializeLoqate();
    // We only want to initialize Loqate once on mount, so we leave the dependency array empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loqateControl != null) {
      loqateControl.listen('error', (err: unknown) => {
        console.log('error', err);
      });
    }
  }, [loqateControl]);

  useEffect(() => {
    if (loqateControl != null) {
      loqateControl.listen('populate', (address: unknown) => {
        if (!isLoqateAddress(address)) return;

        setSelectedStateAbbr(address.Province);

        // set the rest of the address fields
        setContactInfo((prevState: ContactProps) => ({
          ...prevState,
          countyId: address.AdminAreaCode,
          city: address.City,
          zipCode: address.PostalCode,
          streetAddress1: address.Line1,
          streetAddress2: address.Line2,
        }));
      });
    }
  }, [loqateControl]);

  useEffect(() => {
    // Clean zip codes (remove zip4 data)
    if (
      contactInfo.zipCode != null &&
      contactInfo.zipCode !== '' &&
      /^\d{5}-\d{4}$/.test(contactInfo.zipCode)
    ) {
      // Extract the first 5 characters (excluding the hyphen)
      setContactInfo((prevState: ContactProps) => ({
        ...prevState,
        zipCode: prevState.zipCode?.substring(0, 5) ?? prevState.zipCode,
      }));
    }
  }, [contactInfo.zipCode]);

  function submitOnBlur(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setContactInfo((prevState: ContactProps) => ({
      ...prevState,
      [e.target.id]: e.target.value,
      countyId: userType == UserType.NationalProgrammeLead ? '999' : prevState.countyId,
      stateId: userType == UserType.NationalProgrammeLead ? '99' : prevState.stateId,
      countryId: userType == UserType.NationalProgrammeLead ? prevState.countryId : USCountryId,
    }));
  }

  function updatePhoneNumber(updatedPhoneNumber: string) {
    let pe = true;
    let phone = null;
    if (updatedPhoneNumber !== '') {
      pe = !phoneNumberIsValid(updatedPhoneNumber);
      phone = updatedPhoneNumber;
    }
    setPhoneError(pe);
    setContactInfo((prevState: ContactProps) => ({
      ...prevState,
      phone: phone,
    }));
  }

  function isNameValid(name?: string | null): boolean {
    return name !== undefined && name !== null && name.length > 2;
  }

  function handleModalClose() {
    setOpenModal(false);
  }

  const contactAddress: AddressProps = useMemo(() => {
    return {
      city: contactInfo.city,
      countryId: contactInfo.countryId,
      countyId: contactInfo.countyId,
      stateId: contactInfo.stateId,
      streetAddress1: contactInfo.streetAddress1,
      streetAddress2: contactInfo.streetAddress2,
      zipCode: contactInfo.zipCode,
    };
  }, [contactInfo]);

  const handleAddressUpdate = useCallback((address: AddressProps) => {
    setContactInfo((prevState: ContactProps) => ({
      ...prevState,
      streetAddress1: address.streetAddress1,
      streetAddress2: address.streetAddress2,
      city: address.city,
      countyId: address.countyId,
      stateId: address.stateId,
      countryId: address.countryId,
      zipCode: address.zipCode,
    }));
  }, []);

  return (
    <>
      <Dialog open={openModal} onClose={handleModalClose}>
        <DialogTitleWithClose onClose={handleModalClose}>
          There Were Problems With Your Form
        </DialogTitleWithClose>
        <DialogContent>
          <Typography>
            There was a problem with your form. The following questions do not have a valid value:
          </Typography>

          {whichContactFieldsHaveErrors.length > 0 && (
            <Box>
              <Typography variant='h3'>Contact Details</Typography>
              {whichContactFieldsHaveErrors.map((item: RequiredContactField) => {
                return (
                  <Typography key={item} color='error' sx={{ margin: '5px 0px' }}>
                    <Error
                      style={{
                        position: 'relative',
                        width: '20px',
                        height: '20px',
                        top: '4px',
                        marginRight: '10px',
                      }}
                    ></Error>
                    {requiredContactFieldLabels[item]}
                  </Typography>
                );
              })}
            </Box>
          )}

          <Typography>
            Check the values and re-enter if necessary. If using the browser&apos;s autofill for any
            input, please try typing the value instead.
          </Typography>
        </DialogContent>
      </Dialog>

      <RowWrap>
        <TextField
          variant='outlined'
          id='firstName'
          label='First Name'
          value={contactInfo.firstName ?? ''}
          required
          onChange={submitOnBlur}
          error={formSubmitted && !isNameValid(contactInfo.firstName)}
          sx={{ flexGrow: 1 }}
        />

        <TextField
          variant='outlined'
          id='lastName'
          label='Last Name'
          value={contactInfo.lastName ?? ''}
          required
          onChange={submitOnBlur}
          error={formSubmitted && !isNameValid(contactInfo.lastName)}
          sx={{ flexGrow: 1 }}
        />
      </RowWrap>
      <RowWrap>
        <Box
          style={{
            flexGrow: 1,
            flexBasis: 0,
          }}
        >
          <PhoneNumberSelect
            id='phone'
            label='Phone Number'
            phone={contactInfo.phone}
            updatePhoneNumber={updatePhoneNumber}
            showError={
              formSubmitted || (contactInfo?.phone != null && contactInfo.phone.length > 4)
            }
            defaultCountry={countryCode}
            required
          />
        </Box>

        <Email
          variant='outlined'
          id='email'
          label='Email'
          name={'email'}
          sx={{ flexGrow: 1, flexBasis: 0 }}
          value={contactInfo.email ?? ''}
          disabled
          onChange={() => {
            /* nothing - is readonly */
          }}
        />
      </RowWrap>
      <RowWrap>
        <Address
          address={contactAddress}
          onChange={handleAddressUpdate}
          showErrors={formSubmitted}
        />
      </RowWrap>

      <Button
        onClick={leadingDebounce(() => {
          return handleSubmit();
        }, 3000)}
        disabled={submitClicked}
        variant='primary'
        size='large'
      >
        {submitClicked ? 'Submitting...' : 'Submit'}
      </Button>
    </>
  );

  function leadingDebounce(func: () => void, timeout = 500): () => void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return () => {
      if (timer === null) {
        func();
      }
      if (timer !== null) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = null;
      }, timeout);
    };
  }
}

export default ADFSignUp;
